import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import BrandLogo from "../components/BrandLogo";
import { TrendDown, ChartLineUp, ArrowRight } from "@phosphor-icons/react";

export default function Benchmarks() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [floor, setFloor] = useState(3);

    useEffect(() => {
        // SEO meta tags
        document.title = "Debt & Judgment Settlement Benchmarks by Creditor — LienLift Direct";
        const setMeta = (name, content) => {
            let m = document.querySelector(`meta[name="${name}"]`);
            if (!m) { m = document.createElement("meta"); m.setAttribute("name", name); document.head.appendChild(m); }
            m.setAttribute("content", content);
        };
        setMeta("description",
            "Anonymized, aggregated debt and judgment settlement percentages by creditor. " +
            "See what real debtors paid to settle with each major creditor. Updated continuously.");
        setMeta("robots", "index,follow");

        api.get("/intelligence/public-benchmarks?limit=20").then((r) => {
            setRows(r.data.results);
            setFloor(r.data.anonymity_floor);
        }).finally(() => setLoading(false));
    }, []);

    // JSON-LD structured data for search engines
    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Dataset",
        "name": "Debt & Judgment Settlement Benchmarks",
        "description": "Aggregated settlement percentages by creditor from real debt negotiations.",
        "publisher": { "@type": "Organization", "name": "LienLift Direct" },
        "license": "https://creativecommons.org/licenses/by-nc/4.0/",
    };

    return (
        <div className="min-h-screen bg-white font-sans text-slate-900">
            <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(jsonLd)}} />

            {/* Header */}
            <header className="border-b border-slate-200">
                <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <BrandLogo height={36} />
                    </Link>
                    <Link to="/register">
                        <Button data-testid="benchmarks-cta" className="bg-[#0F1E3D] hover:bg-[#1A2C57] h-9">Open a free case</Button>
                    </Link>
                </div>
            </header>

            <main className="max-w-[1200px] mx-auto px-6 py-16">
                <p className="overline">Settlement intelligence · Public benchmarks</p>
                <h1 className="font-serif text-5xl sm:text-6xl mt-4 leading-tight tracking-tight">
                    What creditors <span className="italic text-[#2563EB]">actually accept</span>.
                </h1>
                <p className="mt-6 text-lg text-slate-700 max-w-2xl">
                    Anonymized, aggregated settlement percentages by creditor — pulled from real debt negotiations
                    handled by LienLift Direct clients. Each creditor needs at least <strong>{floor}</strong> verified
                    settlements before they appear here.
                </p>

                <div className="mt-12" data-testid="benchmarks-table">
                    {loading ? (
                        <p className="text-slate-500">Loading…</p>
                    ) : rows.length === 0 ? (
                        <div className="border border-dashed border-slate-300 rounded-md p-16 text-center">
                            <ChartLineUp size={36} weight="duotone" className="mx-auto text-slate-400" />
                            <p className="mt-6 font-serif text-2xl">Benchmarks coming soon</p>
                            <p className="text-slate-600 mt-3 max-w-md mx-auto">
                                We publish a creditor's average only after <strong>{floor}+</strong> independent settlements have been recorded.
                                Be one of the first to contribute — open a case today.
                            </p>
                            <Link to="/register">
                                <Button className="mt-6 bg-[#0F1E3D] hover:bg-[#1A2C57]">
                                    Start a case <ArrowRight size={14} className="ml-2" />
                                </Button>
                            </Link>
                        </div>
                    ) : (
                        <div className="border border-slate-200 rounded-md overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                                    <tr>
                                        <th className="text-left px-5 py-4 font-medium">Rank</th>
                                        <th className="text-left px-5 py-4 font-medium">Creditor</th>
                                        <th className="text-right px-5 py-4 font-medium">Settlements</th>
                                        <th className="text-right px-5 py-4 font-medium">Avg accepted</th>
                                        <th className="text-right px-5 py-4 font-medium">Best deal</th>
                                        <th className="text-right px-5 py-4 font-medium">Total saved</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((r, i) => (
                                        <tr key={r.slug} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`benchmark-row-${r.slug}`}>
                                            <td className="px-5 py-4 font-mono text-slate-500">{String(i + 1).padStart(2, "0")}</td>
                                            <td className="px-5 py-4 font-semibold">
                                                <Link to={`/benchmarks/${r.slug}`} className="text-[#0F1E3D] hover:text-[#2563EB] hover:underline">
                                                    {r.creditor}
                                                </Link>
                                            </td>
                                            <td className="px-5 py-4 text-right font-mono">{r.settled_count}</td>
                                            <td className="px-5 py-4 text-right font-mono font-semibold text-[#0F1E3D]">{r.avg_settlement_pct}%</td>
                                            <td className="px-5 py-4 text-right font-mono text-emerald-700">{r.best_settlement_pct}%</td>
                                            <td className="px-5 py-4 text-right font-mono text-emerald-700 inline-flex items-center justify-end gap-1">
                                                <TrendDown size={12} weight="bold" /> ${r.total_savings.toLocaleString()}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <section className="mt-24 grid grid-cols-1 md:grid-cols-3 gap-8 border-t border-slate-200 pt-16">
                    <div>
                        <p className="overline">Method</p>
                        <p className="text-sm text-slate-700 mt-3 leading-relaxed">
                            Each row is the average of all <em>settled</em> LienLift Direct cases for that creditor.
                            Cases must include both an original balance and a final settled amount to be counted.
                        </p>
                    </div>
                    <div>
                        <p className="overline">Privacy</p>
                        <p className="text-sm text-slate-700 mt-3 leading-relaxed">
                            We apply a <strong>k = {floor}</strong> anonymity floor — creditors below the threshold do
                            not appear. No client data, no jurisdiction, no identifying detail is ever published.
                        </p>
                    </div>
                    <div>
                        <p className="overline">Not legal advice</p>
                        <p className="text-sm text-slate-700 mt-3 leading-relaxed">
                            Past settlements do not predict your outcome. LienLift Direct is a non-legal
                            information &amp; document service, not a law firm.
                        </p>
                    </div>
                </section>
            </main>

            <footer className="border-t border-slate-200">
                <div className="max-w-[1200px] mx-auto px-6 py-8 text-xs text-slate-500 flex flex-col md:flex-row justify-between gap-4">
                    <p>© {new Date().getFullYear()} LienLift Direct · A non-legal judgment information &amp; document service.</p>
                    <Link to="/" className="hover:text-slate-700">Back to home →</Link>
                </div>
            </footer>
        </div>
    );
}
